/**
 * Store State Service
 *
 * Manages the event-log model for streak freezes and time machine repairs.
 * Events are append-only and idempotent via client-generated IDs (setDoc with
 * the event ID rewrites the same immutable payload).
 *
 * Storage layout (Firestore):
 *   users/{userId}/storeFreezeEvents/{eventId}
 *   users/{userId}/storeTmEvents/{eventId}
 *   users/{userId}/storeSettings/main
 *
 * See STORE_SYSTEM.md for the design.
 */

import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  setDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { POINTS_CONFIG } from '../types/gamification';
import type {
  FreezeEvent,
  FreezeInventory,
  FreezeType,
  StoreSettings,
  TmEvent,
} from '../types/store';
import { DEFAULT_WEEKEND_WINDOW, EQUIPMENT_CAP_DAYS, FREEZE_DAYS_COVERAGE, TM_COOLDOWN_DAYS, TM_LOOKBACK_DAYS } from '../types/store';
import { BOX_DEFINITIONS, rollBox, type BoxType, type CosmeticCategory, type RollResult } from '../types/surpriseBoxes';
import { awardPoints, spendPoints } from './PointsManager';

const FREEZE_EVENTS_SUBCOLLECTION = 'storeFreezeEvents';
const TM_EVENTS_SUBCOLLECTION = 'storeTmEvents';
const SETTINGS_SUBCOLLECTION = 'storeSettings';
const SETTINGS_DOC_ID = 'main';

export function newFreezeEventId(): string {
  return `fz_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export function newTmEventId(): string {
  return `tm_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export async function getStoreSettings(userId: string): Promise<StoreSettings> {
  try {
    const docRef = doc(db, 'users', userId, SETTINGS_SUBCOLLECTION, SETTINGS_DOC_ID);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      const data = snap.data() as StoreSettings;
      // Docs written before the weekend selector shipped have no window at
      // all; fill it here so callers never see `undefined` and silently treat
      // it as the default.
      return { ...data, weekendWindow: data.weekendWindow ?? DEFAULT_WEEKEND_WINDOW };
    }
  } catch (error) {
    console.error('Error reading store settings:', error);
  }
  return { weekendWindow: DEFAULT_WEEKEND_WINDOW };
}

/**
 * Patch the settings doc. Field-level merge, NOT a whole-document replace:
 * several long-running flows (challenge evaluation, box opens, cosmetic
 * purchases) read this doc, do slow work, then write. A full replace made
 * every one of those a lost-update window — a stale snapshot could revert
 * the user's weekend window, or drop cosmetics granted in the meantime.
 * Callers must pass ONLY the fields they intend to change.
 *
 * Use clearStoreSettingsFields() to remove a field; merge writes cannot.
 */
export async function saveStoreSettings(
  userId: string,
  settings: Partial<StoreSettings>,
): Promise<void> {
  const docRef = doc(db, 'users', userId, SETTINGS_SUBCOLLECTION, SETTINGS_DOC_ID);
  await setDoc(docRef, settings, { merge: true });
}

/** Delete specific settings fields (merge writes can only add/overwrite). */
export async function clearStoreSettingsFields(
  userId: string,
  keys: Array<keyof StoreSettings>,
): Promise<void> {
  if (keys.length === 0) return;
  const docRef = doc(db, 'users', userId, SETTINGS_SUBCOLLECTION, SETTINGS_DOC_ID);
  const patch: Record<string, unknown> = {};
  for (const key of keys) patch[key as string] = deleteField();
  await setDoc(docRef, patch, { merge: true });
}

/**
 * Cached remote lookup for a user's equipped cursor + frame IDs. Used to
 * render opponents' cosmetics in tournaments without per-frame Firestore
 * reads.
 */
const remoteCosmeticsCache = new Map<string, { cursorId?: string; frameId?: string; expiresAt: number }>();
const REMOTE_COSMETICS_TTL_MS = 5 * 60 * 1000;

export async function getRemoteEquippedCosmetics(
  userId: string,
): Promise<{ cursorId?: string; frameId?: string }> {
  const cached = remoteCosmeticsCache.get(userId);
  if (cached && cached.expiresAt > Date.now()) {
    return { cursorId: cached.cursorId, frameId: cached.frameId };
  }
  try {
    const docRef = doc(db, 'users', userId, SETTINGS_SUBCOLLECTION, SETTINGS_DOC_ID);
    const snap = await getDoc(docRef);
    const data = snap.exists() ? (snap.data() as StoreSettings) : null;
    const result = {
      cursorId: data?.equippedCursorBundleId,
      frameId: data?.equippedAvatarFrameId,
    };
    remoteCosmeticsCache.set(userId, { ...result, expiresAt: Date.now() + REMOTE_COSMETICS_TTL_MS });
    return result;
  } catch (error) {
    console.error(`Failed to load remote cosmetics for ${userId}:`, error);
    return {};
  }
}

export async function getFreezeEvents(userId: string): Promise<FreezeEvent[]> {
  try {
    const colRef = collection(db, 'users', userId, FREEZE_EVENTS_SUBCOLLECTION);
    const snap = await getDocs(colRef);
    return snap.docs.map(d => d.data() as FreezeEvent);
  } catch (error) {
    console.error('Error reading freeze events:', error);
    return [];
  }
}

export async function appendFreezeEvent(
  userId: string,
  event: FreezeEvent,
): Promise<void> {
  const docRef = doc(db, 'users', userId, FREEZE_EVENTS_SUBCOLLECTION, event.id);
  await setDoc(docRef, event);
}

/**
 * Admin-only: hard-delete a single freeze event. Used by the Player History
 * admin tab to correct a wrongly-fired or wrongly-granted freeze. Owners cannot
 * delete events (the log is immutable for them — see firestore.rules).
 */
export async function deleteFreezeEvent(
  userId: string,
  eventId: string,
): Promise<void> {
  const docRef = doc(db, 'users', userId, FREEZE_EVENTS_SUBCOLLECTION, eventId);
  await deleteDoc(docRef);
}

export async function getTmEvents(userId: string): Promise<TmEvent[]> {
  try {
    const colRef = collection(db, 'users', userId, TM_EVENTS_SUBCOLLECTION);
    const snap = await getDocs(colRef);
    return snap.docs.map(d => d.data() as TmEvent);
  } catch (error) {
    console.error('Error reading tm events:', error);
    return [];
  }
}

export async function appendTmEvent(
  userId: string,
  event: TmEvent,
): Promise<void> {
  const docRef = doc(db, 'users', userId, TM_EVENTS_SUBCOLLECTION, event.id);
  await setDoc(docRef, event);
}

/**
 * Admin-only: hard-delete a single time machine event. Used by the Player
 * History admin tab to correct a wrongly-applied repair.
 */
export async function deleteTmEvent(
  userId: string,
  eventId: string,
): Promise<void> {
  const docRef = doc(db, 'users', userId, TM_EVENTS_SUBCOLLECTION, eventId);
  await deleteDoc(docRef);
}

/**
 * Pure: builds a set of date strings (YYYY-MM-DD) covered by either a freeze
 * fire or a TM repair. Use to drive `calculateGlobalStreak`'s `savedDays`.
 */
export function deriveSavedDays(
  freezeEvents: FreezeEvent[],
  tmEvents: TmEvent[],
): Set<string> {
  const saved = new Set<string>();
  for (const e of freezeEvents) {
    if (e.type === 'fired') {
      for (const d of e.savedDays) saved.add(d);
    }
  }
  for (const e of tmEvents) {
    if (e.type === 'repaired') saved.add(e.repairedDay);
  }
  return saved;
}

/**
 * Loads freeze + TM events for a user and returns the set of dates currently
 * "covered" by them. Convenience wrapper for streak-display surfaces.
 */
export async function loadSavedDays(userId: string): Promise<Set<string>> {
  const [freezeEvents, tmEvents] = await Promise.all([
    getFreezeEvents(userId),
    getTmEvents(userId),
  ]);
  return deriveSavedDays(freezeEvents, tmEvents);
}

export function tmCostFor(daysBack: number): number {
  return POINTS_CONFIG.TIME_TRAVEL_BASE + daysBack * POINTS_CONFIG.TIME_TRAVEL_PER_DAY;
}

export function tmCooldownRemainingDays(events: TmEvent[]): number {
  let latest = 0;
  for (const e of events) {
    if (e.type !== 'repaired') continue;
    const ts = Date.parse(e.ts);
    if (ts > latest) latest = ts;
  }
  if (latest === 0) return 0;
  const elapsedMs = Date.now() - latest;
  const cooldownMs = TM_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
  const remainingMs = cooldownMs - elapsedMs;
  if (remainingMs <= 0) return 0;
  return Math.ceil(remainingMs / (24 * 60 * 60 * 1000));
}

export function daysBetween(fromDateStr: string, toDateStr: string): number {
  const from = new Date(fromDateStr + 'T00:00:00').getTime();
  const to = new Date(toDateStr + 'T00:00:00').getTime();
  return Math.round((to - from) / (24 * 60 * 60 * 1000));
}

function todayYmd(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export type PurchaseTimeMachineResult =
  | { success: true; event: TmEvent; cost: number }
  | {
      success: false;
      reason:
        | 'cooldown_active'
        | 'out_of_range'
        | 'today_not_practiced'
        | 'insufficient_points'
        | 'already_saved'
        | 'append_failed';
      cooldownRemainingDays?: number;
    };

/**
 * Purchase a Time Machine repair for a specific missed day. Client-side
 * cooldown enforcement (see STORE_SYSTEM.md §6 — Firestore rules accept any
 * well-formed event).
 */
export async function purchaseTimeMachine(
  userId: string,
  repairedDay: string,
  todayPracticed: boolean,
  savedDaysAlready: Set<string>,
): Promise<PurchaseTimeMachineResult> {
  if (!todayPracticed) {
    return { success: false, reason: 'today_not_practiced' };
  }

  const today = todayYmd();
  const back = daysBetween(repairedDay, today);
  if (back <= 0 || back > TM_LOOKBACK_DAYS) {
    return { success: false, reason: 'out_of_range' };
  }

  if (savedDaysAlready.has(repairedDay)) {
    return { success: false, reason: 'already_saved' };
  }

  const tmEvents = await getTmEvents(userId);
  const cooldown = tmCooldownRemainingDays(tmEvents);
  if (cooldown > 0) {
    return { success: false, reason: 'cooldown_active', cooldownRemainingDays: cooldown };
  }

  const cost = tmCostFor(back);
  const spent = await spendPoints(userId, cost, 'time_machine', { repairedDay, daysBack: back });
  if (!spent) {
    return { success: false, reason: 'insufficient_points' };
  }

  const event: TmEvent = {
    id: newTmEventId(),
    type: 'repaired',
    ts: new Date().toISOString(),
    repairedDay,
    cost,
  };

  try {
    await appendTmEvent(userId, event);
  } catch (error) {
    console.error('TM append failed after spend — points were deducted:', error);
    return { success: false, reason: 'append_failed' };
  }

  return { success: true, event, cost };
}

export type PurchaseFreezeResult =
  | { success: true; event: FreezeEvent }
  | { success: false; reason: 'cap_exceeded' | 'insufficient_points' | 'append_failed' };

export type PurchaseCosmeticResult =
  | { success: true; bundleId: string }
  | { success: false; reason: 'already_owned' | 'insufficient_points' | 'save_failed' };

/**
 * Purchase + auto-equip a cosmetic by ID. Used for cursor bundles, avatar
 * frames, finish effects, and premium avatars. Returns success if the user
 * already owned it (idempotent re-equip).
 *
 * Note for premium avatars: this only owns + bookkeeps in StoreSettings.
 * The caller is responsible for flipping the user profile's customAvatarType
 * to 'premium' (the picker does this via onAvatarChange).
 */
async function purchaseCosmetic(
  userId: string,
  kind: 'cursor' | 'frame' | 'finishEffect' | 'premiumAvatar',
  bundleId: string,
  price: number,
): Promise<PurchaseCosmeticResult> {
  const settings = await getStoreSettings(userId);
  const ownedKey =
    kind === 'cursor' ? 'ownedCursorBundleIds'
    : kind === 'frame' ? 'ownedAvatarFrameIds'
    : kind === 'finishEffect' ? 'ownedFinishEffectIds'
    : 'ownedPremiumAvatarIds';
  const equippedKey =
    kind === 'cursor' ? 'equippedCursorBundleId'
    : kind === 'frame' ? 'equippedAvatarFrameId'
    : kind === 'finishEffect' ? 'equippedFinishEffectId'
    : 'equippedPremiumAvatarId';

  const owned = settings[ownedKey] ?? [];

  if (!owned.includes(bundleId)) {
    if (price > 0) {
      const spent = await spendPoints(userId, price, `${kind}:${bundleId}`, { kind, bundleId });
      if (!spent) {
        return { success: false, reason: 'insufficient_points' };
      }
    }
    owned.push(bundleId);
  }

  try {
    await saveStoreSettings(userId, {
      [ownedKey]: owned,
      [equippedKey]: bundleId,
    });
  } catch (error) {
    console.error(`${kind} save failed:`, error);
    return { success: false, reason: 'save_failed' };
  }

  return { success: true, bundleId };
}

export function purchaseCursorBundle(
  userId: string,
  bundleId: string,
  price: number,
): Promise<PurchaseCosmeticResult> {
  return purchaseCosmetic(userId, 'cursor', bundleId, price);
}

export function purchaseAvatarFrame(
  userId: string,
  frameId: string,
  price: number,
): Promise<PurchaseCosmeticResult> {
  return purchaseCosmetic(userId, 'frame', frameId, price);
}

export function purchaseFinishEffect(
  userId: string,
  effectId: string,
  price: number,
): Promise<PurchaseCosmeticResult> {
  return purchaseCosmetic(userId, 'finishEffect', effectId, price);
}

export function purchasePremiumAvatar(
  userId: string,
  avatarId: string,
  price: number,
): Promise<PurchaseCosmeticResult> {
  return purchaseCosmetic(userId, 'premiumAvatar', avatarId, price);
}

export type UnlockCustomUploadResult =
  | { success: true }
  | { success: false; reason: 'already_unlocked' | 'insufficient_points' | 'save_failed' };

/**
 * One-time unlock for the custom-image avatar. Spends points once and flips the
 * `customUploadUnlocked` flag; after that the user may swap their uploaded
 * picture freely (no per-upload charge). Idempotent if already unlocked.
 */
export async function unlockCustomUpload(
  userId: string,
  price: number,
): Promise<UnlockCustomUploadResult> {
  const settings = await getStoreSettings(userId);
  if (settings.customUploadUnlocked) {
    return { success: false, reason: 'already_unlocked' };
  }

  const spent = await spendPoints(userId, price, 'customUpload', {});
  if (!spent) {
    return { success: false, reason: 'insufficient_points' };
  }

  try {
    await saveStoreSettings(userId, { customUploadUnlocked: true });
  } catch (error) {
    console.error('custom upload unlock save failed:', error);
    return { success: false, reason: 'save_failed' };
  }

  return { success: true };
}

/**
 * Purchase a freeze: enforces the 3-day equipment cap, spends points, then
 * appends a `purchased` event. Client-side validation only — the Firestore
 * rules accept any well-formed event regardless of cap, by design (see
 * STORE_SYSTEM.md §6).
 */
export async function purchaseFreeze(
  userId: string,
  freezeType: FreezeType,
): Promise<PurchaseFreezeResult> {
  const events = await getFreezeEvents(userId);
  const inventory = deriveFreezeInventory(events);
  const addedDays = FREEZE_DAYS_COVERAGE[freezeType];

  if (inventory.totalDaysEquipped + addedDays > EQUIPMENT_CAP_DAYS) {
    return { success: false, reason: 'cap_exceeded' };
  }

  const price = freezeType === 'normal'
    ? POINTS_CONFIG.STREAK_FREEZE_NORMAL
    : POINTS_CONFIG.STREAK_FREEZE_WEEKEND;

  const spent = await spendPoints(userId, price, `freeze:${freezeType}`, { freezeType });
  if (!spent) {
    return { success: false, reason: 'insufficient_points' };
  }

  const event: FreezeEvent = {
    id: newFreezeEventId(),
    type: 'purchased',
    freezeType,
    ts: new Date().toISOString(),
  };

  try {
    await appendFreezeEvent(userId, event);
  } catch (error) {
    console.error('Freeze append failed after spend — points were deducted:', error);
    return { success: false, reason: 'append_failed' };
  }

  // New inventory should be considered by the next reconciler run, even if
  // one already happened today.
  try {
    const { invalidateReconcileMarker } = await import('../utils/streakWithFreezes');
    invalidateReconcileMarker(userId);
  } catch {
    // Non-critical
  }

  return { success: true, event };
}

/**
 * Pure: derives current freeze inventory by walking the event log.
 * Refused events do not change inventory.
 */
export function deriveFreezeInventory(events: FreezeEvent[]): FreezeInventory {
  let normalEquipped = 0;
  let weekendEquipped = 0;

  for (const event of events) {
    if (event.type === 'purchased') {
      if (event.freezeType === 'normal') normalEquipped++;
      else weekendEquipped++;
    } else if (event.type === 'fired') {
      if (event.freezeType === 'normal') normalEquipped--;
      else weekendEquipped--;
    }
  }

  return {
    equipped: { normal: normalEquipped, weekend: weekendEquipped },
    totalDaysEquipped: normalEquipped + weekendEquipped * 2,
  };
}

// ── Surprise boxes ────────────────────────────────────────────────────────

export const OWNED_KEY_BY_CATEGORY: Record<CosmeticCategory, keyof StoreSettings> = {
  cursor: 'ownedCursorBundleIds',
  frame: 'ownedAvatarFrameIds',
  finishEffect: 'ownedFinishEffectIds',
  premiumAvatar: 'ownedPremiumAvatarIds',
  background: 'ownedBackgroundIds',
  gadget: 'ownedGadgetIds',
  presetAvatar: 'ownedPresetAvatarIds',
};

/** All cosmetic IDs the user owns, across every category. */
function ownedCosmeticIds(settings: StoreSettings): Set<string> {
  return new Set<string>([
    ...(settings.ownedCursorBundleIds ?? []),
    ...(settings.ownedAvatarFrameIds ?? []),
    ...(settings.ownedFinishEffectIds ?? []),
    ...(settings.ownedPremiumAvatarIds ?? []),
  ]);
}

export type PurchaseBoxResult =
  | { success: true; boxType: BoxType; cost: number }
  | { success: false; reason: 'not_buyable' | 'insufficient_points' | 'save_failed' };

/** Buy one box of the given type with points. Boxes are not auto-opened. */
export async function purchaseBox(
  userId: string,
  boxType: BoxType,
): Promise<PurchaseBoxResult> {
  const def = BOX_DEFINITIONS[boxType];
  if (def.price == null) {
    return { success: false, reason: 'not_buyable' };
  }

  const settings = await getStoreSettings(userId);

  const spent = await spendPoints(userId, def.price, `box:${boxType}`, { boxType });
  if (!spent) {
    return { success: false, reason: 'insufficient_points' };
  }

  const boxes = { ...(settings.boxes ?? {}) };
  boxes[boxType] = (boxes[boxType] ?? 0) + 1;

  try {
    await saveStoreSettings(userId, { boxes });
  } catch (error) {
    console.error('Box purchase save failed after spend — points were deducted:', error);
    return { success: false, reason: 'save_failed' };
  }

  return { success: true, boxType, cost: def.price };
}

/** Grant boxes without spending points (challenge / admin reward path). */
export async function grantBox(
  userId: string,
  boxType: BoxType,
  count = 1,
): Promise<void> {
  const settings = await getStoreSettings(userId);
  const boxes = { ...(settings.boxes ?? {}) };
  boxes[boxType] = (boxes[boxType] ?? 0) + count;
  await saveStoreSettings(userId, { boxes });
}

export type OpenBoxResult =
  | { success: true; drop: RollResult }
  | { success: false; reason: 'none_owned' | 'save_failed' };

/**
 * Open one box of the given type: rolls an unowned cosmetic (or dust), grants
 * it, decrements the unopened count, and advances the pity counter. The dropped
 * item is added to the owner's inventory but NOT auto-equipped.
 */
export async function openBox(
  userId: string,
  boxType: BoxType,
): Promise<OpenBoxResult> {
  const settings = await getStoreSettings(userId);
  const have = settings.boxes?.[boxType] ?? 0;
  if (have <= 0) {
    return { success: false, reason: 'none_owned' };
  }

  const drop = rollBox(boxType, ownedCosmeticIds(settings), settings.boxPity ?? 0);

  const boxes = { ...(settings.boxes ?? {}) };
  boxes[boxType] = have - 1;

  const next: Partial<StoreSettings> = { boxes, boxPity: drop.pity };

  if (drop.kind === 'item') {
    const key = OWNED_KEY_BY_CATEGORY[drop.item.category];
    const owned = [...((settings[key] as string[] | undefined) ?? [])];
    if (!owned.includes(drop.item.id)) owned.push(drop.item.id);
    (next as unknown as Record<string, string[]>)[key] = owned;
  }

  // Persist the consumed box + grant FIRST so a duplicate open can't replay.
  try {
    await saveStoreSettings(userId, next);
  } catch (error) {
    console.error('Box open save failed:', error);
    return { success: false, reason: 'save_failed' };
  }

  // Dust is awarded after the box is consumed; if this fails the box is still
  // spent (logged, not retried) — mirrors the freeze spend-then-append risk.
  if (drop.kind === 'dust') {
    try {
      await awardPoints(userId, 'bonus', { amount: drop.points, reason: 'box_dust', boxType });
    } catch (error) {
      console.error('Box dust award failed after box was consumed:', error);
    }
  }

  return { success: true, drop };
}
